import React from 'react';

import { ShowroomGetParams } from 'generated/graphql';

import { TrackFn, useTrackApi } from 'lib/trackApi';

export type ContextState = {
	showroomId: string;
	isUserView: boolean;
	track: TrackFn;
	getParams: ShowroomGetParams;
	scrollElement: Element;
	isPublic: boolean;
};

export type ComponentProps = {
	showroomId: string;
	tenant: string;
	isUserView: boolean;
	children: React.ReactElement;
	password?: string;
	scrollElement?: Element;
	isPublic?: boolean;
};

export const ShowroomContext = React.createContext<ContextState>({
	showroomId: null,
	isUserView: false,
	track: null,
	getParams: null,
	scrollElement: null,
	isPublic: false
});

/**
 * Context to share some global variables across the diferent showroom components.
 * @param props - Provides the showroom data, the showroom mode (user view or editing) and the component tree that need the values
 * @returns Component with that provides the showroom global values
 *
 */
const ShowroomContextProvider = (props: ComponentProps) => {
	const { showroomId, tenant, isUserView, children, password, scrollElement = null, isPublic } = props;

	const { track } = useTrackApi('showroom', tenant, isUserView);

	const getParams: ShowroomGetParams = React.useMemo(() => {
		const params: ShowroomGetParams = { id: showroomId };
		if (!isUserView) return params;
		if (password) params.password = password;
		params.tenant = tenant;
		return params;
	}, [isUserView, password, showroomId, tenant]);

	const state: ContextState = React.useMemo(
		() => ({
			showroomId,
			isUserView,
			track,
			getParams,
			scrollElement,
			isPublic
		}),
		[showroomId, isUserView, track, getParams, scrollElement, isPublic]
	);

	return <ShowroomContext.Provider value={state}>{children}</ShowroomContext.Provider>;
};

export default React.memo(ShowroomContextProvider);
